import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { adminService } from '../services/adminService';
import { PublicFlag } from '../types/admin';

const CACHE_KEY = 'contigo:public_flags';
const CACHE_TTL_MS = 60 * 1000; // 60 s (mismo TTL que la caché Redis del backend)

interface FeatureFlagsContextValue {
  flags: PublicFlag[];
  isLoading: boolean;
  isEnabled: (key: string, defaultEnabled?: boolean) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: [],
  isLoading: true,
  isEnabled: () => true,
});

function readCache(): PublicFlag[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { flags, at } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return flags;
  } catch {
    return null;
  }
}

function writeCache(flags: PublicFlag[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ flags, at: Date.now() }));
  } catch {
    // localStorage no disponible: se ignora.
  }
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<PublicFlag[]>(() => readCache() ?? []);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setFlags(cached);
      setIsLoading(false);
      return;
    }

    adminService
      .getPublicFlags()
      .then((res) => {
        setFlags(res.flags);
        writeCache(res.flags);
      })
      .catch(() => {
        // Sin conexión o servidor caído: se mantienen los flags por defecto.
      })
      .finally(() => setIsLoading(false));
  }, []);

  const value = useMemo<FeatureFlagsContextValue>(() => {
    const map = new Map(flags.map((f) => [f.key, Boolean(f.enabled)]));

    return {
      flags,
      isLoading,
      isEnabled: (key, defaultEnabled = true) => map.get(key) ?? defaultEnabled,
    };
  }, [flags, isLoading]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  return useContext(FeatureFlagsContext);
}
